/**
 * Category React Query Hooks
 *
 * Provides data fetching and mutation hooks for category-related operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createDetailHooks } from '@/shared/hooks/create-detail-hooks';
import {
  categoryService,
  type CategoryListParams,
  type CategoryRuleListParams,
} from '@/shared/services/category-service';
import type {
  CategoryCreate,
  CategoryUpdate,
  CategoryRuleCreate,
  CategoryRuleUpdate,
  CategoryRuleTestRequest,
} from '@/shared/types';

/**
 * Query key factory for category queries
 */
export const categoryKeys = {
  all: ['categories'] as const,
  lists: () => [...categoryKeys.all, 'list'] as const,
  list: (params?: CategoryListParams) => [...categoryKeys.lists(), params] as const,
  tree: () => [...categoryKeys.all, 'tree'] as const,
  usage: () => [...categoryKeys.all, 'usage'] as const,
  details: () => [...categoryKeys.all, 'detail'] as const,
  detail: (id: string) => [...categoryKeys.details(), id] as const,
  rules: () => [...categoryKeys.all, 'rules'] as const,
  ruleList: (params?: CategoryRuleListParams) => [...categoryKeys.rules(), 'list', params] as const,
  rule: (id: string) => [...categoryKeys.rules(), 'detail', id] as const,
};

// =============================================================================
// Categories
// =============================================================================

/**
 * Hook to fetch paginated list of categories
 *
 * @param params - Optional filters and pagination
 * @returns Query result with paginated categories
 */
export function useCategories(params?: CategoryListParams) {
  return useQuery({
    queryKey: categoryKeys.list(params),
    queryFn: () => categoryService.list(params),
    staleTime: 30 * 60 * 1000, // Low volatility — categories rarely change // Cache for 5 minutes
  });
}

/**
 * Hook to fetch category tree structure
 *
 * @returns Query result with category tree
 */
export function useCategoryTree() {
  return useQuery({
    queryKey: categoryKeys.tree(),
    queryFn: () => categoryService.getTree(),
    staleTime: 30 * 60 * 1000, // Low volatility — categories rarely change // Cache for 5 minutes
  });
}

/**
 * Hook to fetch transaction usage counts per category
 *
 * @returns Query result with category_id → transaction_count mapping
 */
export function useCategoryUsage() {
  return useQuery({
    queryKey: categoryKeys.usage(),
    queryFn: () => categoryService.getUsage(),
    staleTime: 30 * 60 * 1000, // Low volatility — categories rarely change
  });
}

/**
 * useCategory(id?) — conditional fetch, data may be undefined.
 * useCategorySuspense(id) — suspense fetch, data is guaranteed. Must be inside <Suspense>.
 */
export const [useCategory, useCategorySuspense] = createDetailHooks(
  categoryKeys.detail,
  categoryService.getById,
);

/**
 * Hook to create a new category
 *
 * @returns Mutation for creating categories
 */
export function useCreateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CategoryCreate) => categoryService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: categoryKeys.tree() });
    },
  });
}

/**
 * Hook to update a category
 *
 * @returns Mutation for updating categories
 */
export function useUpdateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CategoryUpdate }) =>
      categoryService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: categoryKeys.tree() });
      queryClient.invalidateQueries({ queryKey: categoryKeys.detail(variables.id) });
    },
  });
}

/**
 * Hook to delete a category
 *
 * @returns Mutation for deleting categories
 */
export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => categoryService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: categoryKeys.tree() });
      queryClient.invalidateQueries({ queryKey: categoryKeys.rules() });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['recurring'] });
    },
  });
}

// =============================================================================
// Category Rules
// =============================================================================

/**
 * Hook to fetch paginated list of category rules
 *
 * @param params - Optional filters and pagination
 * @returns Query result with paginated rules
 */
export function useCategoryRules(params?: CategoryRuleListParams) {
  return useQuery({
    queryKey: categoryKeys.ruleList(params),
    queryFn: () => categoryService.listRules(params),
  });
}

/**
 * useCategoryRule(id?) — conditional fetch, data may be undefined.
 * useCategoryRuleSuspense(id) — suspense fetch, data is guaranteed. Must be inside <Suspense>.
 */
export const [useCategoryRule, useCategoryRuleSuspense] = createDetailHooks(
  categoryKeys.rule,
  categoryService.getRuleById,
);

/**
 * Hook to create a new category rule
 *
 * @returns Mutation for creating rules
 */
export function useCreateCategoryRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CategoryRuleCreate) => categoryService.createRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.rules() });
    },
  });
}

/**
 * Hook to update a category rule
 *
 * @returns Mutation for updating rules
 */
export function useUpdateCategoryRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CategoryRuleUpdate }) =>
      categoryService.updateRule(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.rules() });
      queryClient.invalidateQueries({ queryKey: categoryKeys.rule(variables.id) });
    },
  });
}

/**
 * Hook to delete a category rule
 *
 * @returns Mutation for deleting rules
 */
export function useDeleteCategoryRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => categoryService.deleteRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.rules() });
    },
  });
}

/**
 * Hook to apply a single rule to all uncategorized transactions
 *
 * @returns Mutation with matched_count in the result
 */
export function useApplyCategoryRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => categoryService.applyRule(id),
    onSuccess: () => {
      // Transactions may have gained categories — invalidate transaction lists
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/**
 * Hook to apply all enabled rules to uncategorized transactions
 *
 * @returns Mutation with matched_count in the result
 */
export function useApplyAllCategoryRules() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => categoryService.applyAllRules(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/**
 * Hook to test a category rule pattern against real transactions
 *
 * @returns Mutation with CategoryRuleTestResponse
 */
export function useTestCategoryRule() {
  return useMutation({
    mutationFn: (data: CategoryRuleTestRequest) => categoryService.testRule(data),
  });
}
